import {
  CommissionLine,
  CommissionSettlement,
  LineOfBusiness,
  Ticket,
  TreeMember,
} from '../models/domain.models';
import { COMMISSION_CLAIM_DUE_DAYS, COMMISSION_TOLERANCE } from '../models/rules';
import {
  AgreementRow,
  AgreementsView,
  MissingAgreement,
  MyCommission,
  SplitMemberRow,
  SplitShare,
  SplitView,
  UploadResult,
} from '../models/views.models';
import {
  agreementAt, holderOf, inForce, lineCompanyPays, lineExpected, monthOf, previousPeriod, shareAt, subtreeOids,
} from './commission-math';
import { failure, Outcome } from './outcome';
import { addDays } from './rng';
import { StoreHost } from './store-host';

const PERIOD = /^\d{4}-(0[1-9]|1[0-2])$/;
const money = new Intl.NumberFormat('es-AR');

function roundTo100(n: number): number {
  return Math.round(n / 100) * 100;
}

export interface ManualSettlementBody {
  companyOid: string;
  period: string;
  total: number;
  detail?: { policyNumber: string; amount: number }[];
}

export class CommissionEngine {
  private uploads = 0;

  constructor(private readonly host: StoreHost) {}

  private get db() {
    return this.host.db;
  }

  private me(): TreeMember | null {
    const oid = this.host.user()?.memberOid;
    return this.db.members.find(m => m.oid === oid) ?? null;
  }

  private producerScope(): { me: TreeMember; owners: Set<string> } | null {
    const me = this.me();
    if (!me || this.host.user()?.role !== 'producer') return null;
    return { me, owners: subtreeOids(this.db.members, me.oid) };
  }

  defaultPeriod(): string {
    return previousPeriod(this.host.today());
  }

  // ── Agreements and split (P-67) ───────────────────────────────

  agreementsView(): AgreementsView {
    const scope = this.producerScope();
    if (!scope) return { rows: [], missing: [] };
    const today = this.host.today();
    const producers = this.db.members.filter(m => m.kind === 'producer' && scope.owners.has(m.oid));
    const nameOf = (oid: string) => this.db.members.find(m => m.oid === oid)?.name ?? '—';
    const companyName = (oid: string) => this.db.companies.find(c => c.oid === oid)?.name ?? '—';
    const rows: AgreementRow[] = this.db.agreements
      .filter(a => producers.some(p => p.oid === a.producerOid))
      .map(a => ({
        oid: a.oid, producerOid: a.producerOid, producerName: nameOf(a.producerOid), companyOid: a.companyOid,
        companyName: companyName(a.companyOid), lineOfBusiness: a.lineOfBusiness, ratePct: a.ratePct,
        validFrom: a.validFrom, validTo: a.validTo, current: inForce(a, today),
      }))
      .sort((x, y) => x.producerName.localeCompare(y.producerName) || x.companyName.localeCompare(y.companyName)
        || x.lineOfBusiness.localeCompare(y.lineOfBusiness) || y.validFrom.localeCompare(x.validFrom));
    const counts = new Map<string, MissingAgreement>();
    for (const policy of this.db.policies.filter(p => p.status === 'active' && scope.owners.has(p.ownerOid))) {
      const holder = holderOf(this.db.members, policy.ownerOid);
      if (agreementAt(this.db.agreements, holder, policy.companyOid, policy.lineOfBusiness, today)) continue;
      const key = `${holder}|${policy.companyOid}|${policy.lineOfBusiness}`;
      const found = counts.get(key);
      if (found) found.policies++;
      else {
        counts.set(key, {
          producerOid: holder, producerName: nameOf(holder), companyOid: policy.companyOid,
          companyName: companyName(policy.companyOid), lineOfBusiness: policy.lineOfBusiness, policies: 1,
        });
      }
    }
    const missing = [...counts.values()].sort((x, y) => y.policies - x.policies);
    return { rows, missing };
  }

  saveAgreement(body: {
    producerOid: string; companyOid: string; lineOfBusiness: LineOfBusiness; ratePct: number; validFrom: string;
  }): Outcome<{ oid: string }> {
    const scope = this.producerScope();
    const producer = this.db.members.find(m => m.oid === body.producerOid);
    const company = this.db.companies.find(c => c.oid === body.companyOid);
    if (!scope || !producer || producer.kind !== 'producer' || !scope.owners.has(producer.oid) || !company
      || !company.lines.includes(body.lineOfBusiness)) {
      return failure(400, 'AGREEMENT_INVALID', 'Elegí un productor de tu árbol y una compañía que opere el ramo');
    }
    if (!(body.ratePct > 0 && body.ratePct <= 100) || !/^\d{4}-\d{2}-\d{2}$/.test(body.validFrom ?? '')) {
      return failure(400, 'AGREEMENT_INVALID', 'Indicá una tasa entre 0 y 100 y la fecha desde la que rige');
    }
    const open = this.db.agreements.find(a => a.producerOid === producer.oid && a.companyOid === company.oid
      && a.lineOfBusiness === body.lineOfBusiness && a.validTo === null);
    if (open && body.validFrom <= open.validFrom) {
      return failure(409, 'AGREEMENT_OVERLAP', `La nueva vigencia debe empezar después de la actual (${open.validFrom})`);
    }
    if (open) open.validTo = addDays(body.validFrom, -1);
    const oid = `agr-${producer.oid.slice(4)}-${company.oid.slice(4)}-${body.lineOfBusiness}-${this.host.nextSequence()}`;
    this.db.agreements.push({
      oid, producerOid: producer.oid, companyOid: company.oid, lineOfBusiness: body.lineOfBusiness,
      ratePct: body.ratePct, validFrom: body.validFrom, validTo: null,
    });
    return { value: { oid } };
  }

  splitView(period: string): SplitView {
    const scope = this.producerScope();
    const when = PERIOD.test(period) ? period : this.defaultPeriod();
    if (!scope) return { period: when, members: [] };
    const today = this.host.today();
    const members = this.db.members.filter(m => m.parentOid === scope.me.oid).map((child): SplitMemberRow => {
      const owners = subtreeOids(this.db.members, child.oid);
      const lines = this.db.commissionLines.filter(l => l.period === when && owners.has(l.ownerOid));
      const shares: SplitShare[] = this.db.splits
        .filter(s => s.parentOid === scope.me.oid && s.memberOid === child.oid)
        .sort((x, y) => x.lineOfBusiness.localeCompare(y.lineOfBusiness) || y.validFrom.localeCompare(x.validFrom))
        .map(s => ({
          oid: s.oid, lineOfBusiness: s.lineOfBusiness, sharePct: s.sharePct, validFrom: s.validFrom,
          validTo: s.validTo, current: inForce(s, today),
        }));
      const activeLines = new Set(this.db.policies.filter(p => p.status === 'active' && owners.has(p.ownerOid)).map(p => p.lineOfBusiness));
      const covered = new Set(shares.filter(s => s.current).map(s => s.lineOfBusiness));
      return {
        memberOid: child.oid, name: child.name, kind: child.kind, status: child.status,
        production: lines.reduce((sum, l) => sum + l.collectedPremium, 0),
        amount: this.shareAmount(lines, scope.me.oid, child.oid),
        shares, missingLines: [...activeLines].filter(l => !covered.has(l)),
      };
    });
    return { period: when, members };
  }

  private shareAmount(lines: CommissionLine[], parentOid: string, memberOid: string): number {
    return Math.round(lines.reduce((sum, l) => {
      const expected = lineExpected(this.db.agreements, l);
      const share = shareAt(this.db.splits, parentOid, memberOid, l.lineOfBusiness, l.paidDate);
      return expected !== null && share ? sum + (expected * share.sharePct) / 100 : sum;
    }, 0));
  }

  saveSplit(body: { memberOid: string; lineOfBusiness: LineOfBusiness; sharePct: number; validFrom: string }): Outcome<{ oid: string }> {
    const scope = this.producerScope();
    const member = this.db.members.find(m => m.oid === body.memberOid);
    if (!scope || !member || member.parentOid !== scope.me.oid) {
      return failure(400, 'SPLIT_INVALID', 'El reparto se define para tus hijos y vendedores directos');
    }
    if (!(body.sharePct > 0 && body.sharePct <= 100) || !/^\d{4}-\d{2}-\d{2}$/.test(body.validFrom ?? '')) {
      return failure(400, 'SPLIT_INVALID', 'Indicá un porcentaje entre 0 y 100 y la fecha desde la que rige');
    }
    const open = this.db.splits.find(s => s.parentOid === scope.me.oid && s.memberOid === member.oid
      && s.lineOfBusiness === body.lineOfBusiness && s.validTo === null);
    if (open && body.validFrom <= open.validFrom) {
      return failure(409, 'SPLIT_OVERLAP', `La nueva vigencia debe empezar después de la actual (${open.validFrom})`);
    }
    if (open) open.validTo = addDays(body.validFrom, -1);
    const oid = `spl-${scope.me.oid.slice(4)}-${member.oid.slice(4)}-${body.lineOfBusiness}-${this.host.nextSequence()}`;
    this.db.splits.push({
      oid, parentOid: scope.me.oid, memberOid: member.oid, lineOfBusiness: body.lineOfBusiness,
      sharePct: body.sharePct, validFrom: body.validFrom, validTo: null,
    });
    return { value: { oid } };
  }

  // ── P-68: the caller's own share ──────────────────────────────

  myCommission(period: string): MyCommission {
    const me = this.me();
    const when = PERIOD.test(period) ? period : this.defaultPeriod();
    const empty: MyCommission = {
      period: when, isRoot: true, production: 0, policies: 0, amount: 0, withoutAgreement: 0, shares: [], missingLines: [],
    };
    if (!me) return empty;
    const owners = subtreeOids(this.db.members, me.oid);
    const lines = this.db.commissionLines.filter(l => l.period === when && owners.has(l.ownerOid));
    const base = {
      ...empty, isRoot: !me.parentOid, production: lines.reduce((sum, l) => sum + l.collectedPremium, 0),
      policies: this.db.policies.filter(p => p.status === 'active' && owners.has(p.ownerOid)).length,
    };
    if (!me.parentOid) return base;
    const today = this.host.today();
    const parentOid = me.parentOid;
    const byLine = new Map<LineOfBusiness, CommissionLine[]>();
    lines.forEach(l => byLine.set(l.lineOfBusiness, [...(byLine.get(l.lineOfBusiness) ?? []), l]));
    const shares = [...byLine.entries()].flatMap(([line, group]) => {
      const share = this.db.splits.find(s => s.parentOid === parentOid && s.memberOid === me.oid && s.lineOfBusiness === line && inForce(s, today));
      return share ? [{
        lineOfBusiness: line, sharePct: share.sharePct, validFrom: share.validFrom,
        production: group.reduce((sum, l) => sum + l.collectedPremium, 0), amount: this.shareAmount(group, parentOid, me.oid),
      }] : [];
    });
    const covered = new Set(shares.map(s => s.lineOfBusiness));
    return {
      ...base, shares, amount: shares.reduce((sum, s) => sum + s.amount, 0),
      withoutAgreement: lines.filter(l => lineExpected(this.db.agreements, l) === null).length,
      missingLines: [...byLine.keys()].filter(l => !covered.has(l)),
    };
  }

  // ── Settlements (P-66) and the claim chain (R-07) ─────────────

  private openCandidates(companyOid: string, period: string, owners: Set<string>): CommissionLine[] {
    return this.db.commissionLines.filter(l => l.companyOid === companyOid && l.period === period
      && l.settlementOid === null && owners.has(l.ownerOid));
  }

  private openClaim(line: CommissionLine, settlement: CommissionSettlement): void {
    const me = this.me();
    const expected = lineExpected(this.db.agreements, line);
    if (!me || expected === null || line.settled === null) return;
    const policy = this.db.policies.find(p => p.oid === line.policyOid);
    const today = this.host.today();
    const n = this.host.nextSequence();
    const ticket: Ticket = {
      oid: `tck-${n}`, number: `TK-${String(n).padStart(4, '0')}`, kind: 'commission-claim', category: 'task',
      title: `Reclamo de comisión ${line.period}: póliza ${policy?.number ?? ''}`, status: 'open',
      assigneeOid: me.oid, creatorOid: me.oid, ownerOid: settlement.ownerOid,
      dueDate: addDays(today, COMMISSION_CLAIM_DUE_DAYS), escalated: false, relatedType: 'settlement', relatedOid: settlement.oid,
      personOid: policy?.personOid ?? null, lastActivityDate: today,
      events: [{
        date: today,
        text: `Generado al procesar la liquidación: la compañía pagó $ ${money.format(line.settled)} y el convenio indica $ ${money.format(expected)} `
          + `(diferencia $ ${money.format(expected - line.settled)}, cuota ${line.installmentNumber}).`,
      }],
    };
    this.db.tickets.unshift(ticket);
    line.ticketOid = ticket.oid;
  }

  private processClaims(settlement: CommissionSettlement, lines: CommissionLine[]): number {
    let opened = 0;
    for (const line of lines) {
      const expected = lineExpected(this.db.agreements, line);
      if (line.ticketOid === null && line.settled !== null && expected !== null && line.settled - expected <= -COMMISSION_TOLERANCE) {
        this.openClaim(line, settlement);
        opened++;
      }
    }
    const me = this.me();
    if (me && opened > 0) {
      this.host.notify(me.oid, 'system', `Liquidación procesada: ${opened} diferencia(s) de comisión`,
        'Se generó un ticket de reclamo por cada una. Revisalos en la bandeja.', 'settlement', settlement.oid);
    }
    return opened;
  }

  generateClaim(lineOid: string): Outcome<{ oid: string }> {
    const scope = this.producerScope();
    const line = this.db.commissionLines.find(l => l.oid === lineOid && scope?.owners.has(l.ownerOid));
    const settlement = this.db.settlements.find(s => s.oid === line?.settlementOid);
    if (!scope || !line) return failure(404, 'NOT_FOUND', 'Línea de comisión inexistente');
    const expected = lineExpected(this.db.agreements, line);
    if (!settlement || line.settled === null || expected === null) {
      return failure(409, 'CLAIM_NOT_COMPARABLE', 'Solo se reclama una línea liquidada con detalle y con convenio cargado');
    }
    if (line.ticketOid) return failure(409, 'CLAIM_EXISTS', 'Ya hay un ticket de reclamo para esta línea');
    if (line.settled - expected > -COMMISSION_TOLERANCE) {
      return failure(409, 'CLAIM_BELOW_TOLERANCE', `La diferencia no supera la tolerancia de $ ${money.format(COMMISSION_TOLERANCE)}: no se reclama`);
    }
    this.openClaim(line, settlement);
    return { value: { oid: line.oid } };
  }

  uploadSettlement(body: { companyOid: string; period: string; fileName: string }): Outcome<UploadResult> {
    const scope = this.producerScope();
    const company = this.db.companies.find(c => c.oid === body.companyOid);
    const error = this.validateTarget(scope !== null, company !== undefined, body.period);
    if (error || !scope || !company) return error ?? failure(400, 'SETTLEMENT_INVALID', 'Elegí una compañía');
    if (!body.fileName?.trim()) return failure(400, 'SETTLEMENT_INVALID', 'Elegí el archivo de liquidación');
    const n = ++this.uploads;
    const candidates = this.openCandidates(company.oid, body.period, scope.owners);
    const duplicated = this.db.commissionLines.filter(l => l.companyOid === company.oid && l.period === body.period
      && l.settlementOid !== null && scope.owners.has(l.ownerOid)).length;
    const incomplete = n % 4 === 0 ? 1 : 0;
    const rejected = n % 3 === 0 ? 2 : 0;
    if (candidates.length === 0) {
      return { value: { settlementOid: null, imported: 0, duplicated, incomplete, rejected } };
    }
    const settlement: CommissionSettlement = {
      oid: `set-${company.oid.slice(4)}-${body.period}-${this.host.nextSequence()}`, ownerOid: scope.me.oid,
      companyOid: company.oid, period: body.period, receivedDate: this.host.today(), via: 'file',
      fileName: body.fileName.trim(), settledAmount: 0, unmatched: rejected, hasDetail: true,
    };
    candidates.forEach((line, i) => {
      line.settlementOid = settlement.oid;
      const expected = lineExpected(this.db.agreements, line);
      line.settled = n % 2 === 0 && i === 0 && expected !== null && expected >= 2000
        ? roundTo100(expected * 0.7) : lineCompanyPays(this.db.agreements, line);
    });
    settlement.settledAmount = candidates.reduce((sum, l) => sum + (l.settled ?? 0), 0);
    this.db.settlements.push(settlement);
    this.processClaims(settlement, candidates);
    return { value: { settlementOid: settlement.oid, imported: candidates.length, duplicated, incomplete, rejected } };
  }

  manualSettlement(body: ManualSettlementBody): Outcome<{ oid: string }> {
    const scope = this.producerScope();
    const company = this.db.companies.find(c => c.oid === body.companyOid);
    const error = this.validateTarget(scope !== null, company !== undefined, body.period);
    if (error || !scope || !company) return error ?? failure(400, 'SETTLEMENT_INVALID', 'Elegí una compañía');
    if (!(body.total > 0)) return failure(400, 'SETTLEMENT_INVALID', 'Indicá el total liquidado por la compañía');
    const candidates = this.openCandidates(company.oid, body.period, scope.owners);
    const detail = (body.detail ?? []).filter(d => d.policyNumber?.trim() && d.amount > 0);
    const settlement: CommissionSettlement = {
      oid: `set-${company.oid.slice(4)}-${body.period}-${this.host.nextSequence()}`, ownerOid: scope.me.oid,
      companyOid: company.oid, period: body.period, receivedDate: this.host.today(), via: 'manual', fileName: null,
      settledAmount: body.total, unmatched: 0, hasDetail: detail.length > 0,
    };
    const matched: CommissionLine[] = [];
    if (detail.length > 0) {
      for (const record of detail) {
        const line = candidates.find(l => l.settlementOid === null
          && this.db.policies.find(p => p.oid === l.policyOid)?.number.toLowerCase() === record.policyNumber.trim().toLowerCase());
        if (!line) {
          settlement.unmatched++;
          continue;
        }
        line.settlementOid = settlement.oid;
        line.settled = record.amount;
        matched.push(line);
      }
    } else {
      candidates.forEach(line => {
        line.settlementOid = settlement.oid;
        line.settled = null;
      });
    }
    this.db.settlements.push(settlement);
    this.processClaims(settlement, matched);
    return { value: { oid: settlement.oid } };
  }

  private validateTarget(allowed: boolean, companyExists: boolean, period: string) {
    if (!allowed) return failure(403, 'FORBIDDEN', 'Solo un productor carga liquidaciones');
    if (!companyExists) return failure(400, 'SETTLEMENT_INVALID', 'Elegí una compañía');
    if (!PERIOD.test(period ?? '') || period > monthOf(this.host.today())) {
      return failure(400, 'SETTLEMENT_INVALID', 'Elegí un período (mes) que no sea futuro');
    }
    return null;
  }
}
